import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {MutationObserver | null} */
  #headerStateObserver = null;

  /** @type {ResizeObserver | null} */
  #essentialAnnouncementBarResizeObserver = null;

  /** @type {MutationObserver | null} */
  #essentialAnnouncementBarMountObserver = null;

  /** @type {number | null} */
  #desktopScrollRafId = null;

  static DESKTOP_TOP_BASE_PX = 136;
  static DESKTOP_SCROLL_TOP_THRESHOLD_PX = 140;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {number | null} */
  #sizeSelectionLoadingTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#syncBottomVisibility();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.addEventListener('click', this.#handleSizeOptionClick, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('scroll', this.#handleBottomVisibilityChange, { passive: true, signal });
    window.addEventListener('resize', this.#handleBottomVisibilityChange, { passive: true, signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#headerStateObserver?.disconnect();
    this.#essentialAnnouncementBarResizeObserver?.disconnect();
    this.#essentialAnnouncementBarMountObserver?.disconnect();
    this.#abortController.abort();
    if (this.#desktopScrollRafId !== null) {
      cancelAnimationFrame(this.#desktopScrollRafId);
      this.#desktopScrollRafId = null;
    }
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#sizeSelectionLoadingTimeout) {
      clearTimeout(this.#sizeSelectionLoadingTimeout);
      this.#sizeSelectionLoadingTimeout = null;
    }
  }

  /**
   * Sets up observers and listeners for sticky bar visibility/position.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#hideStickyBar();

    const sectionElement = this.closest('.shopify-section');
    const productDetails = sectionElement?.querySelector('[data-testid="product-information-details"]');
    const visibilityTrigger =
      (productDetails?.querySelector('.product-form-buttons') instanceof HTMLElement
        ? productDetails.querySelector('.product-form-buttons')
        : null) ||
      (productDetails?.querySelector('add-to-cart-component') instanceof HTMLElement
        ? productDetails.querySelector('add-to-cart-component')
        : null) ||
      (productForm.closest('.buy-buttons-block') instanceof HTMLElement ? productForm.closest('.buy-buttons-block') : null);

    if (visibilityTrigger instanceof HTMLElement) {
      this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting) {
          this.#hideStickyBar();
          return;
        }

        const hasPassedTrigger = entry.boundingClientRect.top <= 0;
        if (hasPassedTrigger) {
          this.#showStickyBar();
          return;
        }

        this.#hideStickyBar();
      });

      this.#buyButtonsIntersectionObserver.observe(visibilityTrigger);
    }

    const isDesktop = window.matchMedia('(min-width: 1025px)').matches;

    if (isDesktop) {
      this.#setupEssentialAnnouncementBarObserver();
      this.#syncDesktopTopOffset();

      const { signal } = this.#abortController;
      document.addEventListener('scroll', this.#handleDesktopScroll, { passive: true, signal });
      window.addEventListener('resize', this.#handleDesktopResize, { passive: true, signal });

      const headerComponent = document.querySelector('#header-component');
      if (headerComponent) {
        this.#headerStateObserver = new MutationObserver(() => {
          this.#handleDesktopScroll();
        });

        this.#headerStateObserver.observe(headerComponent, {
          attributes: true,
          attributeFilter: ['data-sticky-state'],
        });
      }

      return;
    }
  }
  
  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.dataset.variantAvailable !== 'true') return;
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    this.#stopSizeSelectionLoading();

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles sticky size option clicks
   * @param {Event} event - The click event
   */
  #handleSizeOptionClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const sizeOption = target?.closest('[data-sticky-size-option]');
    if (!(sizeOption instanceof HTMLButtonElement)) return;

    const dropdown = sizeOption.closest('.sticky-add-to-cart__size-dropdown');
    if (dropdown instanceof HTMLDetailsElement) {
      dropdown.open = false;
    }

    const optionValueId = sizeOption.dataset.optionValueId;
    if (!optionValueId) return;

    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantPicker) return;

    const mainOption = variantPicker.querySelector(`[data-option-value-id="${optionValueId}"]`);
    if (!mainOption) return;

    if (mainOption instanceof HTMLInputElement) {
      if (mainOption.checked) return;
      this.#startSizeSelectionLoading();
      mainOption.checked = true;
      mainOption.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (mainOption instanceof HTMLOptionElement) {
      const select = mainOption.closest('select');
      if (!select) return;
      if (select.value === mainOption.value) return;
      this.#startSizeSelectionLoading();
      select.value = mainOption.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Starts temporary loading state while a new size/variant is resolving.
   */
  #startSizeSelectionLoading() {
    this.refs.addToCartButton.dataset.loading = 'true';

    if (this.#sizeSelectionLoadingTimeout) {
      clearTimeout(this.#sizeSelectionLoadingTimeout);
    }

    this.#sizeSelectionLoadingTimeout = window.setTimeout(() => {
      this.#stopSizeSelectionLoading();
    }, 3000);
  }

  /**
   * Clears size-selection loading state.
   */
  #stopSizeSelectionLoading() {
    this.refs.addToCartButton.removeAttribute('data-loading');

    if (this.#sizeSelectionLoadingTimeout) {
      clearTimeout(this.#sizeSelectionLoadingTimeout);
      this.#sizeSelectionLoadingTimeout = null;
    }
  }

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Keeps the sticky bar hidden on mobile when the page has reached the bottom.
   */
  #syncBottomVisibility() {
    if (!window.matchMedia('(max-width: 1024px)').matches) {
      this.classList.remove('sticky-add-to-cart--bottom-hidden');
      return;
    }

    const scrollingElement = document.scrollingElement || document.documentElement;
    const scrollTop = window.scrollY ?? scrollingElement.scrollTop ?? 0;
    const viewportHeight = window.innerHeight || scrollingElement.clientHeight || 0;
    const scrollHeight = scrollingElement.scrollHeight || document.documentElement.scrollHeight || 0;
    const atBottom = scrollTop + viewportHeight >= scrollHeight - 4;

    this.classList.toggle('sticky-add-to-cart--bottom-hidden', atBottom);
  }

  /**
   * Throttles the bottom visibility sync during scroll/resize.
   */
  #handleBottomVisibilityChange = () => {
    if (this.#desktopScrollRafId !== null) return;

    this.#desktopScrollRafId = requestAnimationFrame(() => {
      this.#desktopScrollRafId = null;
      this.#syncBottomVisibility();
    });
  };

  #handleDesktopScroll = () => {
    if (this.#desktopScrollRafId !== null) return;

    this.#desktopScrollRafId = requestAnimationFrame(() => {
      this.#desktopScrollRafId = null;
      this.#syncDesktopTopOffset();
    });
  };

  /**
   * Keeps the desktop offset in sync without toggling the scroll state.
   */
  #handleDesktopResize = () => {
    if (this.#desktopScrollRafId !== null) return;

    this.#desktopScrollRafId = requestAnimationFrame(() => {
      this.#desktopScrollRafId = null;
      this.#syncDesktopTopOffset();
    });
  };

  /**
   * Essential Countdown app announcement bar (dynamic height).
   * @returns {HTMLElement | null}
   */
  #getEssentialAnnouncementBar() {
    const bar = document.querySelector('.essential_countdown_annoucement_bar_wrapper.top_page');
    return bar instanceof HTMLElement ? bar : null;
  }

  /**
   * @returns {number}
   */
  #getEssentialAnnouncementBarHeight() {
    const bar = this.#getEssentialAnnouncementBar();
    if (!bar) return 0;

    const height = bar.getBoundingClientRect().height;
    return height > 0 ? Math.ceil(height) : 0;
  }

  /**
   * Re-sync desktop offset when the Essential announcement bar mounts or resizes.
   */
  #setupEssentialAnnouncementBarObserver() {
    const attachResizeObserver = (/** @type {HTMLElement} */ bar) => {
      if (this.#essentialAnnouncementBarResizeObserver) return;

      this.#essentialAnnouncementBarResizeObserver = new ResizeObserver(() => {
        this.#handleDesktopResize();
      });
      this.#essentialAnnouncementBarResizeObserver.observe(bar);
      this.#syncDesktopTopOffset();
    };

    const existingBar = this.#getEssentialAnnouncementBar();
    if (existingBar) {
      attachResizeObserver(existingBar);
      return;
    }

    if (this.#essentialAnnouncementBarMountObserver) return;

    this.#essentialAnnouncementBarMountObserver = new MutationObserver(() => {
      const bar = this.#getEssentialAnnouncementBar();
      if (!bar) return;

      this.#essentialAnnouncementBarMountObserver?.disconnect();
      this.#essentialAnnouncementBarMountObserver = null;
      attachResizeObserver(bar);
    });

    this.#essentialAnnouncementBarMountObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Updates the desktop top offset based on scroll position and sticky header state.
   * When scrolled past the threshold the bar sits below the Essential announcement
   * bar (not viewport top) so it does not fight the header scroll-up hide/show cycle.
   */
  #syncDesktopTopOffset() {
    if (!window.matchMedia('(min-width: 1025px)').matches) return;

    const headerComponent = document.querySelector('#header-component');
    const isHeaderSticky = headerComponent?.getAttribute('data-sticky-state') === 'active';
    const scrollTop = window.scrollY ?? document.documentElement.scrollTop ?? 0;
    const announcementHeight = this.#getEssentialAnnouncementBarHeight();
    const desktopTopBase =
      StickyAddToCartComponent.DESKTOP_TOP_BASE_PX + announcementHeight;
    const scrollThreshold =
      StickyAddToCartComponent.DESKTOP_SCROLL_TOP_THRESHOLD_PX + announcementHeight;
    const scrolledPastThreshold = scrollTop > scrollThreshold;

    this.style.setProperty('--essential-announcement-bar-height', `${announcementHeight}px`);
    this.classList.toggle('sticky-add-to-cart--header-active', isHeaderSticky);

    if (isHeaderSticky) {
      this.style.setProperty(
        '--sticky-add-to-cart-desktop-top',
        `calc(var(--header-height, 0px) + ${announcementHeight}px)`
      );
      return;
    }

    const desktopTop = scrolledPastThreshold
      ? `${announcementHeight}px`
      : `${desktopTopBase}px`;

    this.style.setProperty('--sticky-add-to-cart-desktop-top', desktopTop);
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = this.dataset.variantAvailable === 'true';

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
